export const getLocation = () => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported by your browser'));
        } else {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    });
                },
                (error) => {
                    reject(error);
                }
            );
        }
    });
};

export const getIP = async () => {
    try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        return data.ip;
    } catch (error) {
        console.error('Error fetching IP:', error);
        return 'Unknown';
    }
};

/**
 * Calculates the distance between two points in meters using the Haversine formula
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
};

/**
 * Checks if the current location is within any of the allowed geofences
 * @param {Object} currentLoc - { latitude, longitude }
 * @param {Array} allowedLocations - Array of location objects with latitude, longitude, and radius
 * @returns {Object} - { allowed: boolean, nearestLocation: string, distance: number }
 */
export const checkGeofence = (currentLoc, allowedLocations) => {
    if (!allowedLocations || allowedLocations.length === 0) {
        // If no locations are configured, we might want to default to allowing or blocking.
        // Usually, if none are assigned, we allow web-punch, but for geofencing feature, 
        // we'll assume it's enforced if locations exist.
        return { allowed: true }; 
    }

    let nearest = null;
    let minDistance = Infinity;

    for (const loc of allowedLocations) {
        if (!loc.latitude || !loc.longitude) continue;
        
        const dist = calculateDistance(
            currentLoc.latitude,
            currentLoc.longitude,
            parseFloat(loc.latitude),
            parseFloat(loc.longitude)
        );

        if (dist <= (loc.radius || 100)) {
            return { allowed: true, nearestLocation: loc.name, distance: dist };
        }

        if (dist < minDistance) {
            minDistance = dist;
            nearest = loc.name;
        }
    }

    return { allowed: false, nearestLocation: nearest, distance: minDistance };
};

/**
 * Formats distance into a human-readable string (m or km)
 */
export const formatDistance = (meters) => {
    if (meters >= 1000) {
        return `${(meters / 1000).toFixed(2)}km`;
    }
    return `${Math.round(meters)}m`;
};
